import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { DownloadSimple, Sparkle, Heart, CaretDown, CaretUp } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { errText } from '../../utils/errors';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ScorecardReport: tabla pivote ancha con dos pestanas (Cuestionarios clinicos
 * y Satisfaccion). Genera el preview consumiendo /reports/scorecard y permite
 * descargarlo como Excel via /reports/scorecard.xlsx.
 */
export default function ScorecardReport({ studyId, studyCode, lang = 'es' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState('clinical');
  // Qsalud extras: catalog from /config/volunteer-fields + persistent selection
  const STORAGE_KEY = `scorecard_qsalud_extras_${studyId}`;
  const [qsaludCatalog, setQsaludCatalog] = useState([]);
  const [selectedQsalud, setSelectedQsalud] = useState(() => {
    try {
      const raw = localStorage.getItem(`scorecard_qsalud_extras_${studyId}`);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [qsaludOpen, setQsaludOpen] = useState(false);

  const lb = lang === 'es'
    ? {
        title: 'Scorecard',
        subtitle: 'Respuestas de cuestionarios completados por voluntarios incluidos. La edad se calcula a fecha de hoy. No se incluyen las respuestas del cuestionario de Inclusion/Exclusion.',
        tab_clinical: 'Cuestionarios',
        tab_satisfaction: 'Satisfaccion',
        export: 'Exportar a Excel',
        v_code: 'V-code',
        age: 'Edad',
        gender: 'Sexo',
        no_data: 'No hay datos para mostrar.',
        no_volunteers: 'No hay voluntarios incluidos en este estudio.',
        loading: 'Calculando informe...',
        meta_volunteers: 'Voluntarios incluidos',
        meta_questions: 'Preguntas',
        meta_generated: 'Generado',
        qsalud_panel: 'Campos extra del Qsalud',
        qsalud_hint: 'Anade columnas desde la ficha de salud del voluntario en este scorecard. Tu seleccion se recuerda por estudio.',
        qsalud_none: 'No hay campos disponibles.',
        qsalud_selected: 'seleccionados',
        clear_all: 'Limpiar',
      }
    : {
        title: 'Scorecard',
        subtitle: 'Responses of completed questionnaires by included volunteers. Age is computed at today. Inclusion/Exclusion questionnaire is not included.',
        tab_clinical: 'Questionnaires',
        tab_satisfaction: 'Satisfaction',
        export: 'Export to Excel',
        v_code: 'V-code',
        age: 'Age',
        gender: 'Sex',
        no_data: 'No data to display.',
        no_volunteers: 'No included volunteers in this study.',
        loading: 'Computing report...',
        meta_volunteers: 'Included volunteers',
        meta_questions: 'Questions',
        meta_generated: 'Generated',
        qsalud_panel: 'Extra Qsalud fields',
        qsalud_hint: 'Add columns from the volunteer health form into this scorecard. Your selection is remembered per study.',
        qsalud_none: 'No fields available.',
        qsalud_selected: 'selected',
        clear_all: 'Clear',
      };

  // Load Qsalud field catalog once
  useEffect(() => {
    axios.get(`${API_URL}/api/config/volunteer-fields`, { withCredentials: true })
      .then(r => {
        const fields = (r.data || [])
          .filter(f => f.tab === 'health_questionnaire' && f.active)
          .sort((a, b) => (a.order || 0) - (b.order || 0));
        setQsaludCatalog(fields);
      })
      .catch(() => setQsaludCatalog([]));
  }, []);

  // Persist selection per study
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedQsalud)); } catch {}
  }, [selectedQsalud, STORAGE_KEY]);

  // Restore selection when studyId changes
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      setSelectedQsalud(raw ? JSON.parse(raw) : []);
    } catch { setSelectedQsalud([]); }
  }, [STORAGE_KEY]);

  useEffect(() => {
    if (!studyId) return;
    setLoading(true);
    const q = selectedQsalud.length ? `?qsalud=${encodeURIComponent(selectedQsalud.join(','))}&lang=${lang}` : `?lang=${lang}`;
    axios.get(`${API_URL}/api/studies/${studyId}/reports/scorecard${q}`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(err => toast.error(errText(err)))
      .finally(() => setLoading(false));
  }, [studyId, selectedQsalud, lang]);

  const toggleQsalud = (key) => {
    setSelectedQsalud(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };
  const clearQsalud = () => setSelectedQsalud([]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const q = selectedQsalud.length ? `?qsalud=${encodeURIComponent(selectedQsalud.join(','))}&lang=${lang}` : `?lang=${lang}`;
      const r = await axios.get(`${API_URL}/api/studies/${studyId}/reports/scorecard.xlsx${q}`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const blob = new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeCode = (studyCode || studyId || 'study').replace(/[^A-Za-z0-9_-]/g, '_');
      const ts = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, '');
      a.download = `scorecard_${safeCode}_${ts}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(errText(err));
    }
    setExporting(false);
  };

  const tab = activeTab === 'clinical' ? data?.clinical : data?.satisfaction;

  // Compute column groupings for the multi-row header (visit / questionnaire / code).
  const groupings = useMemo(() => {
    if (!tab?.columns) return { visitRuns: [], qstRuns: [] };
    const cols = tab.columns;
    const runsBy = (key) => {
      const out = [];
      cols.forEach((c, idx) => {
        const k = key(c);
        if (out.length && out[out.length - 1].k === k) {
          out[out.length - 1].end = idx;
        } else {
          out.push({ k, start: idx, end: idx, label: c });
        }
      });
      return out;
    };
    return {
      visitRuns: runsBy(c => c.visit_id),
      qstRuns: runsBy(c => `${c.visit_id}__${c.questionnaire_id}`),
      questionRuns: runsBy(c => `${c.visit_id}__${c.question_id || c.q_code}`),
    };
  }, [tab]);

  if (loading) {
    return <p className="text-sm text-stone-500 text-center py-12">{lb.loading}</p>;
  }
  if (!data) return null;

  const totalQuestions = (tab?.columns || []).filter(c => c.kind === 'main').length;

  return (
    <div className="space-y-4" data-testid="scorecard-report">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold text-stone-800 flex items-center gap-2">
            <Sparkle size={18} className="text-[#1f3e80]" />
            {lb.title}
          </h3>
          <p className="text-xs text-stone-500 mt-0.5 max-w-3xl">{lb.subtitle}</p>
        </div>
        <Button
          data-testid="scorecard-export"
          onClick={handleExport}
          disabled={exporting}
          className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm gap-2"
        >
          <DownloadSimple size={16} />
          {exporting ? '...' : lb.export}
        </Button>
      </div>

      {/* Metadata strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-stone-50 border border-stone-200 rounded-sm px-4 py-3 text-xs">
        <div>
          <p className="text-stone-500">{lb.meta_volunteers}</p>
          <p className="font-semibold text-stone-800">{(tab?.rows || []).length}</p>
        </div>
        <div>
          <p className="text-stone-500">{lb.meta_questions}</p>
          <p className="font-semibold text-stone-800">{totalQuestions}</p>
        </div>
        <div className="sm:col-span-2">
          <p className="text-stone-500">{lb.meta_generated}</p>
          <p className="font-mono text-stone-700">{new Date(data.generated_at).toLocaleString(lang === 'es' ? 'es-ES' : 'en-US')}</p>
        </div>
      </div>

      {/* Qsalud extras panel */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-sm" data-testid="qsalud-extras-panel">
        <button
          type="button"
          onClick={() => setQsaludOpen(v => !v)}
          className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-emerald-100/50 transition-colors"
          data-testid="qsalud-extras-toggle"
        >
          <div className="flex items-center gap-2">
            <Heart size={16} className="text-emerald-700" weight="fill" />
            <span className="text-sm font-semibold text-emerald-900">{lb.qsalud_panel}</span>
            {selectedQsalud.length > 0 && (
              <Badge variant="outline" className="text-[10px] bg-emerald-100 text-emerald-800 border-emerald-300">
                {selectedQsalud.length} {lb.qsalud_selected}
              </Badge>
            )}
          </div>
          {qsaludOpen ? <CaretUp size={16} className="text-emerald-700" /> : <CaretDown size={16} className="text-emerald-700" />}
        </button>
        {qsaludOpen && (
          <div className="px-4 py-3 border-t border-emerald-200 space-y-3">
            <p className="text-xs text-emerald-800">{lb.qsalud_hint}</p>
            {qsaludCatalog.length === 0 ? (
              <p className="text-xs text-stone-400 italic">{lb.qsalud_none}</p>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-1.5 max-h-64 overflow-y-auto pr-2">
                  {qsaludCatalog.map(f => {
                    const label = lang === 'es' ? (f.label_es || f.field_key) : (f.label_en || f.label_es || f.field_key);
                    const checked = selectedQsalud.includes(f.field_key);
                    return (
                      <label key={f.field_key} className="flex items-center gap-2 cursor-pointer text-xs py-0.5">
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => toggleQsalud(f.field_key)}
                          data-testid={`qsalud-extra-${f.field_key}`}
                        />
                        <span className="text-stone-700">{label}</span>
                      </label>
                    );
                  })}
                </div>
                {selectedQsalud.length > 0 && (
                  <div className="flex justify-end">
                    <Button variant="ghost" size="sm" onClick={clearQsalud} className="h-7 text-xs text-emerald-700 hover:bg-emerald-100"
                      data-testid="qsalud-extras-clear">
                      {lb.clear_all}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-stone-100">
          <TabsTrigger value="clinical" data-testid="scorecard-tab-clinical">
            {lb.tab_clinical}
            <span className="ml-1.5 text-[10px] text-stone-400 font-mono">({data.clinical.columns.length})</span>
          </TabsTrigger>
          <TabsTrigger value="satisfaction" data-testid="scorecard-tab-satisfaction">
            {lb.tab_satisfaction}
            <span className="ml-1.5 text-[10px] text-stone-400 font-mono">({data.satisfaction.columns.length})</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="clinical" className="mt-3">
          <ScorecardTable tab={data.clinical} groupings={activeTab === 'clinical' ? groupings : null} lb={lb} qsaludFields={data.qsalud_fields || []} />
        </TabsContent>
        <TabsContent value="satisfaction" className="mt-3">
          <ScorecardTable tab={data.satisfaction} groupings={activeTab === 'satisfaction' ? groupings : null} lb={lb} qsaludFields={data.qsalud_fields || []} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ScorecardTable({ tab, groupings, lb, qsaludFields = [] }) {
  // Recompute groupings if not passed (when this tab is not the active one)
  const finalGroupings = useMemo(() => {
    if (groupings) return groupings;
    if (!tab?.columns) return { visitRuns: [], qstRuns: [] };
    const cols = tab.columns;
    const runsBy = (key) => {
      const out = [];
      cols.forEach((c, idx) => {
        const k = key(c);
        if (out.length && out[out.length - 1].k === k) out[out.length - 1].end = idx;
        else out.push({ k, start: idx, end: idx, label: c });
      });
      return out;
    };
    return {
      visitRuns: runsBy(c => c.visit_id),
      qstRuns: runsBy(c => `${c.visit_id}__${c.questionnaire_id}`),
      // URS-F449b: agrupacion adicional por PREGUNTA para el HTML preview.
      // Permite hacer colSpan del enunciado y renderizar una fila extra
      // debajo con los sub_labels (Otalgia, Prurito, Tumefaccion, Otorrea...).
      questionRuns: runsBy(c => `${c.visit_id}__${c.question_id || c.q_code}`),
    };
  }, [tab, groupings]);

  // URS-F451b: medir alturas reales de las <tr> del thead para calcular
  // el `top` acumulado de cada row sticky. Evita solapes con las filas de
  // datos cuando el enunciado (row 3) se envuelve en varias lineas.
  const row1Ref = useRef(null);
  const row2Ref = useRef(null);
  const row3Ref = useRef(null);
  const [stickyTops, setStickyTops] = useState({ r2: 36, r3: 68, r4: 148 });
  useLayoutEffect(() => {
    const r1h = row1Ref.current?.offsetHeight || 36;
    const r2h = row2Ref.current?.offsetHeight || 32;
    const r3h = row3Ref.current?.offsetHeight || 80;
    setStickyTops({ r2: r1h, r3: r1h + r2h, r4: r1h + r2h + r3h });
  }, [tab, qsaludFields.length]);

  if (!tab) return null;

  if (tab.rows.length === 0) {
    return <p className="text-sm text-stone-400 italic text-center py-12">{lb.no_volunteers}</p>;
  }
  if (tab.columns.length === 0 && qsaludFields.length === 0) {
    return <p className="text-sm text-stone-400 italic text-center py-12">{lb.no_data}</p>;
  }

  const COL_W = 210; // fixed width in px for question columns
  const QSALUD_W = 140; // fixed width for qsalud columns

  // Compute sticky left offsets for the dynamic qsalud columns
  // V-code: 68, Edad: 48, Sexo: 48 => offsets 0, 68, 116, 164 + (i*140)
  const baseOffset = 164; // 68 + 48 + 48
  const stickyOffset = (i) => baseOffset + i * QSALUD_W;
  // URS-F367b: width total explicito para que table-layout:fixed respete las
  // anchuras del colgroup. Sin esto, V-code/Edad/Sexo se expanden hasta
  // ocupar el espacio del primer cuestionario y se ven solapadas.
  const totalWidth = baseOffset + qsaludFields.length * QSALUD_W + tab.columns.length * COL_W;

  return (
    <div className="bg-white border border-stone-200 rounded-sm overflow-x-auto" data-testid="scorecard-table-wrapper">
      <table className="text-xs border-collapse" style={{ tableLayout: 'fixed', width: `${totalWidth}px` }}>
        <colgroup>
          <col style={{ width: 68 }} />
          <col style={{ width: 48 }} />
          <col style={{ width: 48 }} />
          {qsaludFields.map(f => (
            <col key={`qsalud-col-${f.field_key}`} style={{ width: QSALUD_W }} />
          ))}
          {tab.columns.map((_, i) => (
            <col key={i} style={{ width: COL_W }} />
          ))}
        </colgroup>
        <thead>
          {/* Row 1: visits — V-code/Edad/Sexo se repiten en las 3 filas del
              header (sin rowSpan) para evitar el bug de sticky + rowSpan que
              causa overlap visual con la primera columna de preguntas.
              URS-F451: sticky top-0 (con top acumulado por fila) para que
              el header permanezca visible al hacer scroll vertical. */}
          <tr ref={row1Ref}>
            <th className="sticky left-0 top-0 bg-[#1f3e80] text-white border border-[#1f3e80] px-2 py-1.5 z-40 align-middle">{lb.v_code}</th>
            <th className="sticky left-[68px] top-0 bg-[#1f3e80] text-white border border-[#1f3e80] px-2 py-1.5 z-40 align-middle">{lb.age}</th>
            <th className="sticky left-[116px] top-0 bg-[#1f3e80] text-white border border-[#1f3e80] px-2 py-1.5 z-40 align-middle">{lb.gender}</th>
            {qsaludFields.length > 0 && (
              <th
                colSpan={qsaludFields.length}
                className="sticky top-0 bg-emerald-700 text-white border border-emerald-700 px-2 py-1.5 text-center font-semibold align-middle z-30"
              >
                Qsalud
              </th>
            )}
            {finalGroupings.visitRuns.map(run => (
              <th
                key={`v-${run.start}`}
                colSpan={run.end - run.start + 1}
                className="sticky top-0 bg-[#1f3e80] text-white border border-[#1f3e80] px-2 py-1.5 text-center font-semibold align-middle z-30"
              >
                {run.label.visit_label}
              </th>
            ))}
          </tr>
          {/* Row 2: questionnaires (empty for qsalud) */}
          <tr ref={row2Ref}>
            <th className="sticky left-0 bg-[#1f3e80] border border-[#1f3e80] z-40" style={{ top: stickyTops.r2 }} />
            <th className="sticky left-[68px] bg-[#1f3e80] border border-[#1f3e80] z-40" style={{ top: stickyTops.r2 }} />
            <th className="sticky left-[116px] bg-[#1f3e80] border border-[#1f3e80] z-40" style={{ top: stickyTops.r2 }} />
            {qsaludFields.length > 0 && (
              <th colSpan={qsaludFields.length} className="sticky bg-emerald-100 border border-emerald-200 z-30" style={{ top: stickyTops.r2 }} />
            )}
            {finalGroupings.qstRuns.map(run => (
              <th
                key={`q-${run.start}`}
                colSpan={run.end - run.start + 1}
                className="sticky bg-blue-100 text-[#1f3e80] border border-blue-200 px-2 py-1 text-center font-semibold align-middle z-30"
                style={{ top: stickyTops.r2 }}
              >
                {run.label.questionnaire_name}
              </th>
            ))}
          </tr>
          {/* Row 3: enunciado de la pregunta (merged sobre sub-columnas) +
              qsalud field labels. Sticky top acumulado (~68px) para
              permanecer visible al scrollar. */}
          <tr ref={row3Ref}>
            <th className="sticky left-0 bg-[#1f3e80] border border-[#1f3e80] z-40" style={{ top: stickyTops.r3 }} />
            <th className="sticky left-[68px] bg-[#1f3e80] border border-[#1f3e80] z-40" style={{ top: stickyTops.r3 }} />
            <th className="sticky left-[116px] bg-[#1f3e80] border border-[#1f3e80] z-40" style={{ top: stickyTops.r3 }} />
            {qsaludFields.map((f, i) => (
              <th
                key={`qsalud-th-${f.field_key}`}
                rowSpan={2}
                className="sticky bg-emerald-50 border border-emerald-200 px-2 py-1.5 text-center font-semibold text-emerald-900 align-middle z-30"
                style={{ left: stickyOffset(i), top: stickyTops.r3, wordWrap: 'break-word', whiteSpace: 'normal' }}
              >
                <span className="block leading-snug">{f.label}</span>
              </th>
            ))}
            {finalGroupings.questionRuns.map(run => {
              const hasSub = tab.columns.slice(run.start, run.end + 1).some(c => (c.sub_label || '').trim());
              const span = run.end - run.start + 1;
              return (
                <th
                  key={`qtext-${run.start}`}
                  colSpan={span}
                  rowSpan={hasSub ? 1 : 2}
                  className="sticky bg-stone-50 border border-stone-200 px-2 py-1.5 text-center font-semibold text-stone-700 align-middle z-30"
                  style={{ top: stickyTops.r3, wordWrap: 'break-word', whiteSpace: 'normal', verticalAlign: 'middle' }}
                >
                  <span className="block leading-snug">{run.label.q_text}</span>
                </th>
              );
            })}
          </tr>
          {/* Row 4 (URS-F449b): sub_label por sub-columna (Otalgia, Prurito,
              Tumefaccion...). Preguntas simples ya extendieron su header
              con rowSpan=2 en la fila anterior. Sticky ~148px. */}
          <tr>
            <th className="sticky left-0 bg-[#1f3e80] border border-[#1f3e80] z-40" style={{ top: stickyTops.r4 }} />
            <th className="sticky left-[68px] bg-[#1f3e80] border border-[#1f3e80] z-40" style={{ top: stickyTops.r4 }} />
            <th className="sticky left-[116px] bg-[#1f3e80] border border-[#1f3e80] z-40" style={{ top: stickyTops.r4 }} />
            {finalGroupings.questionRuns.map(run => {
              const subs = tab.columns.slice(run.start, run.end + 1);
              const hasSub = subs.some(c => (c.sub_label || '').trim());
              if (!hasSub) return null; // ya cubierto por rowSpan=2 arriba
              return subs.map((col, i) => (
                <th
                  key={`sub-${run.start}-${i}`}
                  className={`sticky border border-stone-200 px-2 py-1 text-center font-medium align-middle z-30 ${col.kind === 'composite_total' ? 'bg-amber-100 text-amber-900' : 'bg-stone-100 text-stone-600'}`}
                  style={{ top: stickyTops.r4, wordWrap: 'break-word', whiteSpace: 'normal' }}
                >
                  <span className="block leading-snug text-[11px]">{col.sub_label || ''}</span>
                </th>
              ));
            })}
          </tr>
        </thead>
        <tbody>
          {[...tab.rows].sort((a, b) => {
            const na = parseInt((a.v_code || '').replace(/[^\d]/g, ''), 10);
            const nb = parseInt((b.v_code || '').replace(/[^\d]/g, ''), 10);
            const aHas = Number.isFinite(na), bHas = Number.isFinite(nb);
            if (aHas && bHas && na !== nb) return na - nb;
            if (aHas && !bHas) return -1;
            if (!aHas && bHas) return 1;
            return (a.v_code || '').localeCompare(b.v_code || '');
          }).map(row => (
            <tr key={row.volunteer_id} className="hover:bg-stone-50">
              <td className="sticky left-0 bg-white border border-stone-200 px-2 py-1 font-mono font-semibold text-[#1f3e80] z-10 align-middle">
                <div className="flex flex-col items-start gap-0.5">
                  <span>{row.v_code}</span>
                  {/* URS-F371: badge si el voluntario salió del estudio */}
                  {row.status === 'excluido' && <span className="text-[9px] px-1 rounded-sm bg-red-50 text-red-600 border border-red-200 font-sans font-medium">Excluido</span>}
                  {row.status === 'abandono' && <span className="text-[9px] px-1 rounded-sm bg-amber-50 text-amber-700 border border-amber-200 font-sans font-medium">Abandono</span>}
                </div>
              </td>
              <td className="sticky left-[68px] bg-white border border-stone-200 px-2 py-1 text-center text-stone-600 z-10 align-middle">{row.age || '—'}</td>
              <td className="sticky left-[116px] bg-white border border-stone-200 px-2 py-1 text-center text-stone-600 z-10 align-middle">{row.gender || '—'}</td>
              {(row.qsalud || []).map((qv, i) => (
                <td
                  key={`qsalud-td-${i}`}
                  className="sticky bg-emerald-50/40 border border-emerald-100 px-2 py-1 text-stone-700 align-top z-10"
                  style={{ left: stickyOffset(i), wordWrap: 'break-word', whiteSpace: 'normal' }}
                  data-testid={`qsalud-cell-${row.v_code}-${i}`}
                >
                  {qv || ''}
                </td>
              ))}
              {row.cells.map((cell, i) => {
                const col = tab.columns[i] || {};
                const isTotal = col.kind === 'composite_total';
                return (
                  <td
                    key={i}
                    className={`border border-stone-200 px-2 py-1 align-top ${isTotal ? 'bg-amber-100 text-amber-900 font-semibold' : 'text-stone-700'}`}
                    style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}
                  >
                    {cell || ''}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
